/**
 * 🩹 PATCH-351 (2026-02) · Public Events Page
 * -------------------------------------------
 * Lists every currently-published, non-past event with cover image,
 * date/time, location, description, download link for the PDF flyer,
 * external "Learn more" link, and RSVP mailto.
 *
 * Pattern-matched to the Donate page shell (Header + hero + rows +
 * Footer) so the marketing spine stays consistent.
 */
import { useEffect, useState } from "react";
import { MetalWordmark } from "../components/MetalWordmark";
import axios from "axios";
import {
  Calendar,
  Clock,
  MapPin,
  Download,
  ArrowRight,
  Mail,
  Sparkles,
  ChevronLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function PublicEventsPage() {
  useDocumentMeta({
    title:       "Major Events · The Exchange Alliance",
    description: "Upcoming galas, community forums, workforce workshops, and partner events hosted or co-hosted by The Exchange Alliance.",
    canonical:   "https://theexchangealliance.com/events",
  });

  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/public/events`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="public-events-page" className="bg-[#FAFAF7] text-[#0A0612] font-sans min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="pt-24 pb-14 border-b border-black/5">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] hover:text-[#0A0612] mb-6"
          >
            <ChevronLeft className="h-3 w-3" /> Back to home
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-3">
            <MetalWordmark small testId="events-wordmark" />
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
            Major Events
          </h1>
          <p className="text-lg text-[#3A3A3A] leading-relaxed max-w-2xl">
            Galas, community forums, workforce workshops, and partner
            gatherings where the Alliance mission meets the neighborhood face
            to face. RSVP details — and printable flyers — live here.
          </p>
        </div>
      </section>

      {/* LIST */}
      <section className="flex-1 py-14">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          {loading ? (
            <div data-testid="events-loading" className="text-center text-[#6B6F76] font-mono text-[11px] tracking-[0.22em] uppercase py-16">
              Loading events…
            </div>
          ) : events.length === 0 ? (
            <div data-testid="events-empty" className="text-center py-20 border border-dashed border-[#E8E5DA] bg-white/60">
              <Sparkles className="h-6 w-6 mx-auto text-[#D4AF37] mb-4" />
              <div className="font-display font-bold text-2xl mb-2">No events scheduled right now</div>
              <p className="text-[#6B6F76] max-w-md mx-auto">
                We publish new gatherings a few weeks in advance. Subscribe to
                the Alliance Bulletin below and we&apos;ll drop the next one in
                your inbox.
              </p>
            </div>
          ) : (
            <div className="space-y-10" data-testid="events-list">
              {events.map((ev, i) => (
                <EventRow key={ev.id} event={ev} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function EventRow({ event: ev, index }) {
  const cover = ev.cover_url ? `${process.env.REACT_APP_BACKEND_URL}${ev.cover_url}` : null;
  const attach = ev.attachment_url ? `${process.env.REACT_APP_BACKEND_URL}${ev.attachment_url}` : null;
  return (
    <article
      data-testid={`event-row-${ev.id}`}
      className="grid lg:grid-cols-12 gap-6 lg:gap-10 bg-white border border-[#E8E5DA] p-6 lg:p-8"
    >
      {/* Cover */}
      <div className="lg:col-span-5">
        {cover ? (
          <a
            href={cover}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`event-cover-${ev.id}`}
            className="block"
          >
            {/* 🩹 PATCH-354 · Natural aspect ratio, no frame, no background.
                Uploaded flyers often ship with their own decorative
                borders / backgrounds — a container box was visually
                clashing. Now the image renders bare at its true shape. */}
            <img
              src={cover}
              alt={ev.title}
              className="block w-full h-auto max-h-[600px] object-contain"
            />
          </a>
        ) : (
          <div
            className="bg-gradient-to-br from-[#0A0612] to-[#1a1030] text-[#D4AF37] flex items-center justify-center border border-black/5"
            style={{ aspectRatio: "4 / 5" }}
          >
            <div className="text-center px-6">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase mb-3 opacity-70">
                Event {String(index + 1).padStart(2, "0")}
              </div>
              <div className="font-display font-bold text-2xl leading-tight">{ev.title}</div>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="lg:col-span-7 flex flex-col">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-sm">
          <span className="inline-flex items-center gap-2 text-[#7C5A00] font-mono text-[10px] tracking-[0.22em] uppercase">
            <Calendar className="h-3.5 w-3.5" /> {fmtDate(ev.start_at)}
          </span>
          {ev.start_at && (
            <span className="inline-flex items-center gap-2 text-[#6B6F76] font-mono text-[10px] tracking-[0.22em] uppercase">
              <Clock className="h-3.5 w-3.5" /> {fmtTime(ev.start_at)}
              {ev.end_at ? ` – ${fmtTime(ev.end_at)}` : ""}
            </span>
          )}
          {ev.price_label && (
            <span className="inline-flex items-center gap-2 bg-[#FAF8F0] border border-[#E5DEC8] px-2 py-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C5A00]">
              {ev.price_label}
            </span>
          )}
        </div>

        <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight mb-2">
          {ev.title}
        </h2>
        {ev.subtitle && (
          <div className="text-[#3A3A3A] italic mb-4">{ev.subtitle}</div>
        )}

        {(ev.location_name || ev.location_address) && (
          <div className="mb-4 text-sm text-[#3A3A3A]">
            <div className="inline-flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-[#D4AF37] shrink-0" />
              <div>
                {ev.location_name && <div className="font-semibold">{ev.location_name}</div>}
                {ev.location_address && <div className="text-[#6B6F76]">{ev.location_address}</div>}
              </div>
            </div>
          </div>
        )}

        {ev.description && (
          <p className="text-[#3A3A3A] leading-relaxed mb-6 whitespace-pre-line">
            {ev.description}
          </p>
        )}

        {/* CTAs */}
        <div className="mt-auto flex flex-wrap gap-3 pt-2">
          {ev.external_url && (
            <a
              href={ev.external_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`event-external-${ev.id}`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-none bg-[#0A0612] text-white hover:bg-[#1a1030] font-mono text-[11px] tracking-[0.22em] uppercase"
            >
              Learn more <ArrowRight className="h-3.5 w-3.5" />
            </a>
          )}
          {attach && (
            <a
              href={attach}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`event-attachment-${ev.id}`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-none bg-white border border-[#D4AF37] text-[#0A0612] hover:bg-[#FAF8F0] font-mono text-[11px] tracking-[0.22em] uppercase"
            >
              <Download className="h-4 w-4" /> Download flyer
            </a>
          )}
          {ev.rsvp_email && (
            <a
              href={`mailto:${ev.rsvp_email}?subject=${encodeURIComponent("RSVP · " + ev.title)}`}
              data-testid={`event-rsvp-${ev.id}`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-none border border-[#0A0612]/20 text-[#0A0612] hover:bg-[#FAFAF7] font-mono text-[11px] tracking-[0.22em] uppercase"
            >
              <Mail className="h-4 w-4" /> RSVP
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
